import { IErrorApiResponse } from '@wyz/types';
import { notify } from '@wyz/ui';
import { Trans } from '~/common/context';
import { PurchaseErrorCodes } from '~/common/helpers';
import { useManageCart } from '~/purchase/api';
import { usePurchaseContext } from '~/purchase/context';
import { CheckoutTimer, PurchaseTracking } from '~/purchase/helpers';
import {
  ICartValidationErrorResponse,
  IPayloadAddItemsCart,
} from '~/purchase/types';

type TrackingMeta = {
  ean: string;
  brand: string;
  dimension: string;
  unitPrice: number;
};

type UsePurchaseCartOptions = {
  onQuantityRejected?: () => void;
  onItemRemoved?: (cartItemId: string) => void;
  onAddToCartFailed?: () => void;
  onSubmitSuccess?: (references: string[]) => void;
  onSubmitError?: (error: ICartValidationErrorResponse) => void;
  enabled?: {
    get?: boolean;
    add?: boolean;
    patch?: boolean;
    remove?: boolean;
    reset?: boolean;
    submit?: boolean;
    refresh?: boolean;
  };
};

export const usePurchaseCart = (
  customerId: string,
  options?: UsePurchaseCartOptions,
) => {
  const { isCallCenterMode } = usePurchaseContext();

  const cart = useManageCart({
    context: { customerId },
    get: { enabled: options?.enabled?.get ?? true },
    add: { enabled: options?.enabled?.add ?? true },
    patch: { enabled: options?.enabled?.patch ?? true },
    remove: { enabled: options?.enabled?.remove ?? true },
    reset: { enabled: options?.enabled?.reset ?? true },
    submit: { enabled: options?.enabled?.submit ?? true },
    refresh: { enabled: options?.enabled?.refresh ?? false },
  });

  const getCartTotal = () => cart.get.data?.data.totalExclTax ?? 0;

  const handlePatchError = async (error: IErrorApiResponse) => {
    if (error.errors.includes(PurchaseErrorCodes.ERR_CART_EMPTY_QUANTITY)) {
      options?.onQuantityRejected?.();
      await cart.get.refetch();
    } else if (
      error.errors.includes(
        PurchaseErrorCodes.ERR_CART_MODIFIED_BY_ANOTHER_PROCESS,
      )
    ) {
      await cart.get.refetch();
    }
  };

  const handleRemoveError = async (error: IErrorApiResponse) => {
    if (
      error.errors.includes(
        PurchaseErrorCodes.ERR_CART_MODIFIED_BY_ANOTHER_PROCESS,
      )
    ) {
      await cart.get.refetch();
    }
  };

  const handleAddError = async (error: IErrorApiResponse) => {
    if (error.message === PurchaseErrorCodes.ERR_INVALID_CART) {
      await cart.get.refetch();
    }
    options?.onAddToCartFailed?.();
  };

  return {
    cart,
    isCallCenterMode,

    addToCart: {
      process: (
        payload: IPayloadAddItemsCart,
        product: { ean: string; dimension: string; brand: string },
        totalQuantity: number,
        displayPrice: number,
      ) => {
        cart.add.mutate(payload, {
          onSuccess: () => {
            PurchaseTracking.trackAddedToCart(
              product.ean,
              product.dimension,
              product.brand,
              displayPrice,
              totalQuantity,
              getCartTotal(),
            );
            notify.success(
              <Trans i18nKey='app.purchase.cart.success.order' />,
              { id: 'toast-success-purchase-cart-add-to-cart' },
            );
          },
          onError: handleAddError,
        });
      },
      isLoading: cart.add.isLoading,
    },

    updateQuantity: {
      process: (itemId: string, quantity: number, meta: TrackingMeta) => {
        cart.patch.mutate(
          { itemId, quantity },
          {
            onSuccess: () => {
              PurchaseTracking.trackCartQuantityChanged(
                meta.ean,
                quantity,
                meta.unitPrice,
                meta.brand,
                meta.dimension,
                getCartTotal(),
              );
            },
            onError: handlePatchError,
          },
        );
      },
      isLoading: cart.patch.isLoading,
    },

    removeItem: {
      process: (cartItemId: string, meta: Pick<TrackingMeta, 'ean'>) => {
        cart.remove.mutate([cartItemId], {
          onSuccess: () => {
            PurchaseTracking.trackCartItemRemoved(meta.ean, getCartTotal());
            options?.onItemRemoved?.(cartItemId);
          },
          onError: handleRemoveError,
        });
      },
      isLoading: cart.remove.isLoading,
    },

    clearCart: {
      process: () => {
        cart.reset.mutate(undefined, {
          onSuccess: () => {
            PurchaseTracking.trackCartCleared();
          },
        });
      },
      isLoading: cart.reset.isLoading,
    },

    submitOrder: {
      process: (params: {
        comment?: string;
        submitCartContext: 'DEFAULT' | 'CALL_CENTER';
      }) => {
        cart.submit.mutate(params, {
          onSuccess: (response) => {
            const totalExclTax = cart.get.data?.data.totalExclTax ?? 0;
            PurchaseTracking.trackOrderConfirmed(response.data, totalExclTax);

            const durationMs = CheckoutTimer.readCheckoutDurationMs();
            if (durationMs !== null) {
              PurchaseTracking.trackCheckoutDuration(durationMs);
            }
            CheckoutTimer.clearCheckoutTimer();

            options?.onSubmitSuccess?.(response.data);
          },
          onError: (error) => {
            options?.onSubmitError?.(error);
          },
        });
      },
      isLoading: cart.submit.isLoading,
    },
  };
};
